import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const dot = (color = "#1d4ed8") =>
  L.divIcon({
    className: "",
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2.5px solid white;box-shadow:0 1px 4px rgba(0,0,0,.4);"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });

/** Ren Leaflet-wrapper (utan react-leaflet). markers: [{lat,lng,label,href,color}], layers: [{name,color,features,visible}] */
export default function LeafletMap({ center = [59.33, 18.06], zoom = 11, markers = [], layers = [], wmsLayers = [], onMapClick, height = "h-[420px]", testid = "os-map" }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const markerGroup = useRef(null);
  const layerGroups = useRef({});
  const wmsGroups = useRef({});
  const clickRef = useRef(onMapClick);
  clickRef.current = onMapClick;

  useEffect(() => {
    const map = L.map(elRef.current, { scrollWheelZoom: true }).setView(center, zoom);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);
    map.on("click", (e) => clickRef.current && clickRef.current(e.latlng));
    mapRef.current = map;
    return () => map.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (markerGroup.current) markerGroup.current.remove();
    const g = L.layerGroup().addTo(map);
    markers.forEach((m) => {
      const mk = L.marker([m.lat, m.lng], { icon: dot(m.color) }).addTo(g);
      if (m.label) mk.bindPopup(m.href ? `<a href="${m.href}"><b>${m.label}</b></a>` : `<b>${m.label}</b>`);
    });
    markerGroup.current = g;
    if (markers.length > 1) {
      map.fitBounds(L.latLngBounds(markers.map((m) => [m.lat, m.lng])).pad(0.25));
    } else if (markers.length === 1) {
      map.setView([markers[0].lat, markers[0].lng], 14);
    }
  }, [markers]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    Object.values(layerGroups.current).forEach((g) => g.remove());
    layerGroups.current = {};
    layers.forEach((l) => {
      if (!l.visible) return;
      const g = L.geoJSON(
        { type: "FeatureCollection", features: l.features.map((f) => ({ type: "Feature", geometry: f.geometry, properties: f.attributes || {} })) },
        {
          style: { color: l.color, weight: 2, fillOpacity: 0.15 },
          pointToLayer: (_, latlng) => L.marker(latlng, { icon: dot(l.color) }),
          onEachFeature: (feat, lyr) => {
            const attrs = Object.entries(feat.properties || {}).map(([k, v]) => `${k}: ${v}`).join("<br/>");
            lyr.bindPopup(`<b>${l.name}</b>${attrs ? "<br/>" + attrs : ""}`);
          },
        }
      ).addTo(map);
      layerGroups.current[l.name] = g;
    });
  }, [layers]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    wmsLayers.forEach((w) => {
      const existing = wmsGroups.current[w.id];
      if (w.visible && !existing) {
        wmsGroups.current[w.id] = L.tileLayer.wms(w.url, {
          layers: w.layers,
          format: "image/png",
          transparent: true,
          version: "1.3.0",
          opacity: w.opacity ?? 0.65,
          attribution: w.attribution || "",
        }).addTo(map);
      } else if (!w.visible && existing) {
        existing.remove();
        delete wmsGroups.current[w.id];
      }
    });
  }, [wmsLayers]);

  return <div ref={elRef} data-testid={testid} className={`w-full ${height} rounded-sm border border-zinc-200 z-0`} />;
}
